import './StudioView.scss';
import React, { Fragment, Suspense, RefObject, createRef } from 'react';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { getTranslate, getActiveLanguage } from 'react-localize-redux';
import { serviceLocator } from 'infrastructure/ServiceLocator';
import StudioHeader from './StudioHeader';
import RatePlansView from './RatePlansView';
import ErrorBoundary from 'components/ErrorBoundary';
import routes from 'library/Routes';
import LayoutHelper from 'infrastructure/LayoutHelper';
import UserHelper from 'infrastructure/UserHelper';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core/styles';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import {
  Link, Typography, Grid, Card, CardHeader, CardContent,
  CardActionArea, CardMedia, Slide, Tooltip, Fab, Button,
  Modal, Paper, Divider, Icon, ExpansionPanel,
  ExpansionPanelDetails, ExpansionPanelSummary
} from '@material-ui/core';
import QRCode from 'qrcode';
import BookingDialog from '../Widgets/BookingDialog';
import ContactStudioForm from './ContactStudioForm';
import AddReviewForm from './AddReviewForm';
import classnames from 'classnames';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import StarIcon from '@material-ui/icons/Star';
import SearchIcon from '@material-ui/icons/Search';
import PhoneIcon from '@material-ui/icons/Phone';
import MailIcon from '@material-ui/icons/Mail';
import LanguageIcon from '@material-ui/icons/Language';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import CheckIcon from '@material-ui/icons/Check';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import moment from 'moment';
import { RouteComponentProps } from 'react-router-dom';

const StudioScheduleView = React.lazy(() => import('./StudioScheduleView'));
const StudioPromotionEvents = React.lazy(() =>
  import('./StudioPromotionEvents')
);
const VooptyMapComponent = React.lazy(() => import('./VooptyMapComponent'));
const YouTube = React.lazy(() => import('components/YouTube'));

const PARAMETER_TO_OPEN_CONTACT_FORM = 'contactForm';
const GELIOS = 'GELIOS';

interface Studio {
  id: string;
  _id?: string;
  name: string;
  domainName: string;
  owner_id: string;
  slogan?: string;
  description?: string;
  shortInfo?: string;
  avatar?: string;
  background?: string;
  address?: string;
  email?: string;
  phone?: string;
  site?: string;
  viber?: string;
  telegram?: string;
  facebook?: string;
  instagram?: string;
  twitter?: string;
  youtube?: string;
  categories: any[];
  tags: string[];
  regions: any[];
  city: string;
  country: string;
  gallery?: string[];
  hasPricing?: boolean;
  hasLocation?: boolean;
  geo_with_lat_lon?: {
    lat: number;
    lon: number;
  };
  prices?: {
    _id: string;
    text: string;
    value: number | string;
  }[];
  ratingValue?: number;
  reviewCount?: number;
  hasSchedule?: boolean;
  hasCourses?: boolean;
  hideSchedule?: boolean;
  tutoring?: boolean;
  remoteEducation?: boolean;
  inclusive?: boolean;
  specialNeeds?: boolean;
  veteran?: boolean;
  vooptyPromocode?: string;
  videoReview?: string;
  callToAction?: string;
  additionalContactInfo?: string;
  pricing?: string;
  trialPricing?: string;
  teachersInfo?: string;
  showGenericDialogOnSignUp?: boolean;
  managedByVoopty?: boolean;
  themeColors?: string[];
  [key: string]: any;
}

interface Teacher {
  _id: string;
  name: string;
  studioIds?: string[];
  title?: string;
  description?: string;
  avatar: string;
  [key: string]: any;
}

interface Owner {
  [key: string]: any;
}

interface Review {
  reviewType: 'VerifiedClient' | 'Client' | 'Owner';
  rank?: number;
  created: string;
  creatorName?: string;
  anonymous?: boolean;
  content: string;
  [key: string]: any;
}

interface GroupedReview {
  reviews: Review[];
}

interface ClientReviews {
  groupedReviews: GroupedReview[];
}

interface OtherStudio {
  id: string;
  domainName: string;
  name: string;
  avatar?: string;
  address?: string;
}

interface ContactStudioInfo {
  domain: string;
  [key: string]: any;
}

interface StudioSuggestion {
  category: {
    name: string;
    parent: {
      name: string;
    };
  };
  total: number;
  items: OtherStudio[];
}

interface RouteParams {
  domainName: string;
}

interface StudioViewProps extends WithSnackbarProps, WithStyles<typeof styles>, RouteComponentProps<RouteParams> {
  translate: (key: string, data?: any) => string;
  currentLanguage: string;
  user: any;
  domain: {
    domain: {
      id: string;
      adsense?: boolean;
      custom?: boolean;
      currencyCode: string;
    };
    staticUrl: string;
    owner?: {
      organizationSettings?: {
        clientCabinet?: string;
      };
    };
  };
  studio?: Studio;
  toStudio: (studio: Studio) => void;
  registerToClass: (studio: Studio, classId: string) => void;
}

interface StudioViewState {
  studio?: Studio;
  ownerDomain?: any;
  owner?: Owner;
  notifications: any[];
  showFullDescription: boolean;
  showFullPricing: boolean;
  showSubscribeConfirm: boolean;
  showSubscribeConfirmed: boolean;
  showUnsubscribeConfirm: boolean;
  showUnsubscribeConfirmed: boolean;
  showRegisterToClass: boolean;
  repeatableClass?: any;
  categoryLinks: any[];
  reviews: any[];
  clientReviews?: ClientReviews;
  showContactStudio: boolean;
  contactStudioInfo: ContactStudioInfo;
  contactingStudio: boolean;
  canContact: boolean;
  loadingRatePlans: boolean;
  showCallToAction: boolean;
  showBookingDialog?: boolean;
  showAddReview?: boolean;
  teachers?: Teacher[];
  teachersMap?: Record<string, Teacher>;
  showTeachers?: boolean;
  otherSuggestions?: StudioSuggestion[];
  otherStudiosFromOwner?: OtherStudio[];
}

class StudioView extends React.Component<StudioViewProps, StudioViewState> {
  constructor(props: StudioViewProps) {
    super(props);
    const pageState = (window as any).__PAGE_STATE__;
    let studio, ownerDomain, owner;
    if (pageState) {
      studio = pageState.studio;
      if (studio) {
        if (studio.domainName !== props.match.params.domainName) {
          studio = undefined;
        } else {
          ownerDomain = pageState.ownerDomain;
          owner = pageState.studioOwner;
        }
      }
    }
    this.state = {
      studio: studio,
      ownerDomain: ownerDomain,
      owner: owner,
      notifications: [],
      showFullDescription: false,
      showFullPricing: false,
      showSubscribeConfirm: false,
      showSubscribeConfirmed: false,
      showUnsubscribeConfirm: false,
      showUnsubscribeConfirmed: false,
      showRegisterToClass: false,
      categoryLinks: [],
      reviews: [],
      showContactStudio: false,
      contactStudioInfo: this.newContactStudioInfo(),
      contactingStudio: false,
      canContact: false,
      loadingRatePlans: true,
      showCallToAction: false,
    };
  }

  componentDidMount() {
    const clientCabinet = this.props.domain?.owner?.organizationSettings?.clientCabinet;
    if (clientCabinet === GELIOS) {
      this.props.history.push(routes.studioClientClassesGelios());
    }

    setTimeout(this.callToAction, 30000);
    if (this.state.studio) {
      this.generateQrCode();
    }

    //window.addEventListener("hashchange", this.updateScrollPosition, false);
  }

  // componentWillMount was deprecated in React 16.9
  // Using componentDidMount instead and fixing the logic
  UNSAFE_componentWillMount() {
    if (!this.state.studio) {
      let api = serviceLocator.studioApi;
      let domainName = this.props.match.params.domainName;
      return api
        .getStudioByDomainName(domainName)
        .then(
          this.onSuccess,
          LayoutHelper.onErrSnack(this.props.enqueueSnackbar)
        );
    } else {
      let studio = this.state.studio;
      this.onSuccess(studio);
    }
  }

  componentDidUpdate() {
    //this.updateScrollPosition();
  }

  componentWillUnmount() {
    window.removeEventListener('hashchange', this.updateScrollPosition, false);
  }

  loadOwnerInfo = (studio: Studio) => {
    if (!this.state.owner) {
      serviceLocator.userApi.getOwnerInfo(studio.owner_id).then((owner: Owner) => {
        this.setState({ owner: owner });
      }, LayoutHelper.onErrSnack(this.props.enqueueSnackbar));
    }
  };

  getThemeColor = (index: number) => {
    if (this.state.studio?.themeColors && this.state.studio.themeColors[index]) {
      return this.state.studio.themeColors[index];
    }
  };

  callToAction = () => {
    if (this.state.studio && this.state.studio.callToAction) {
      if (
        !this.state.showBookingDialog &&
        !this.state.showContactStudio &&
        !this.state.showAddReview &&
        window.location.pathname ===
          routes.studio({ domainName: this.state.studio.domainName })
      ) {
        this.setState({ showCallToAction: true });
      }
    }
  };

  renderCallToAction = () => {
    if (!this.state.studio) return null;
    
    return (
      <Modal
        className={this.props.classes.modal}
        open={this.state.showCallToAction}
        onClose={() => {
          this.setState({ showCallToAction: false });
        }}
      >
        <Slide
          direction="up"
          in={this.state.showCallToAction}
          mountOnEnter
          unmountOnExit
        >
          <div className={this.props.classes.callToActionGrid}>
            <Grid container justify="center" spacing={2}>
              <Grid item xs={12}>
                <Typography align="center">
                  {this.state.studio.callToAction}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography align="center">
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => {
                      this.openBookingDialog();
                    }}
                  >
                    {this.props.translate('studio.joinTheClass')}
                  </Button>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <ul
                  className={classnames(
                    this.props.classes.ctaIconHold,
                    this.props.classes.iconjump
                  )}
                >
                  {this.state.studio.viber && (
                    <li className={this.props.classes.iconLi}>
                      <Link
                        className={classnames(
                          this.props.classes.iconA,
                          'lazyload'
                        )}
                        data-bgset={`${this.props.domain.staticUrl}/icon-viber.png`}
                        href={`viber://chat?number=${this.state.studio.viber}`}
                      ></Link>
                    </li>
                  )}
                  {this.state.studio.telegram && (
                    <li className={this.props.classes.iconLi}>
                      <Link
                        className={classnames(
                          this.props.classes.iconA,
                          'lazyload'
                        )}
                        data-bgset={`${this.props.domain.staticUrl}/icon-telegram.png`}
                        href={this.state.studio.telegram}
                      ></Link>
                    </li>
                  )}
                  {this.state.studio.phone && (
                    <li className={this.props.classes.iconLi}>
                      <Link
                        className={classnames(
                          this.props.classes.iconA,
                          'lazyload'
                        )}
                        data-bgset={`${this.props.domain.staticUrl}/icon-phone.png`}
                        href={`tel:${this.state.studio.phone}`}
                      ></Link>
                    </li>
                  )}
                </ul>
              </Grid>
            </Grid>
          </div>
        </Slide>
      </Modal>
    );
  };

  loadOtherSuggestions = (studio: Studio) => {
    if (!this.props.domain.domain.custom) {
      let api = serviceLocator.studioApi;

      api.getOtherSuggestions(studio.id).then((data: StudioSuggestion[]) => {
        this.setState({ otherSuggestions: data });
      }, LayoutHelper.onErrSnack(this.props.enqueueSnackbar));
    }
  };

  loadTeachers = (studio: Studio) => {
    let api = serviceLocator.teacherApi;

    api.getPublicTeachers(studio.owner_id, studio._id).then((data: { teachers: Teacher[] }) => {
      let teachers = data.teachers;
      let teachersMap: Record<string, Teacher> = {};
      for (let i = 0; i < teachers.length; i++) {
        let teacher = teachers[i];
        teachersMap[teacher._id] = teacher;
      }
      const showTeachers = teachers.length <= 8 ? true : false;
      this.setState({
        teachers: teachers,
        teachersMap,
        showTeachers: showTeachers,
      });
      const hash = window.location.hash;
      if (hash) {
        if (
          !this.state.studio?.showGenericDialogOnSignUp &&
          (hash === '#courses' || hash === '#schedule')
        ) {
          this.openBookingDialog();
        }
      }
    }, LayoutHelper.onErrSnack(this.props.enqueueSnackbar));
  };

  loadPublicStudios = (studio: Studio) => {
    let api = serviceLocator.studioApi;
    if (studio.managedByVoopty) {
      api.getPublicStudioByOwnerId(studio.owner_id).then((data: { items: OtherStudio[] }) => {
        let otherStudiosFromOwner: OtherStudio[] = [];
        let items = data.items;
        for (let i = 0; i < items.length; i++) {
          let otherStudio = items[i];
          if (otherStudio.id !== studio.id) {
            otherStudiosFromOwner.push(otherStudio);
          }
        }
        this.setState({ otherStudiosFromOwner: otherStudiosFromOwner });
      }, LayoutHelper.onErrSnack(this.props.enqueueSnackbar));
    }
  };

  updateScrollPosition = () => {
    try {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
          });
        }
      } else {
        window.scrollTo(0, 0);
      }
    } catch (err) {
      console.error(err);
    }
  }

  onSuccess = (studio: Studio) => {
    this.loadOwnerInfo(studio);
    let categoryLinks: any[] = [];
    for (let i = 0; i < studio.categories.length; i++) {
      let category = studio.categories[i];
      categoryLinks.push(category);
    }
    this.setState({ studio: studio, categoryLinks: categoryLinks });
    this.generateQrCode();
    this.loadTeachers(studio);
    this.loadReviews(studio);
    this.loadOtherSuggestions(studio);
    this.loadPublicStudios(studio);
    this.handleUrlParams();
  };

  handleUrlParams() {
    const location = this.props.location;
    const queryParams = new URLSearchParams(location.search);

    this.handleOpenContactFormByUrlParam(
      queryParams.get(PARAMETER_TO_OPEN_CONTACT_FORM)
    );
  }

  handleOpenContactFormByUrlParam(value: string | null) {
    if (value == null) {
      // value is unset
      return;
    }
    this.openContactStudio();
  }

  loadReviews = (studio: Studio) => {
    let api = serviceLocator.reviewApi;

    // api.getVooptyReviews(studio.id).then((reviews) => {
    //     this.setState({reviews: reviews})
    api
      .getClientReviews(studio.id, this.props.domain.domain.custom)
      .then((clientReviews: ClientReviews) => {
        this.setState({ clientReviews: clientReviews });
      }, LayoutHelper.onErrSnack(this.props.enqueueSnackbar));
    // }, LayoutHelper.onErrSnack(this.props.enqueueSnackbar));
  };

  generateQrCode = () => {
    QRCode.toCanvas(
      window.location.href,
      { errorCorrectionLevel: 'H' },
      function (err, canvas) {
        if (err) {
          console.log(err);
          throw err;
        }

        let container = document.getElementById('qr-container');
        if (container) {
          container.appendChild(canvas);
        }
      }
    );
  };

  render() {
    return (
      <div>
        {this.state.studio
          ? this.renderStudio(this.state.studio)
          : LayoutHelper.renderLoader()}
      </div>
    );
  }

  googleMapRef: RefObject<HTMLDivElement> = createRef();

  renderPricesTable = () => {
    if (!this.state.studio || !this.state.studio.prices || this.state.studio.prices.length === 0) {
      return null;
    }
    
    const domain = this.state.ownerDomain || this.props.domain.domain;
    return (
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={this.props.translate('studio.pricing')}
          ></CardHeader>
          <Divider
            style={{
              backgroundColor: this.getThemeColor(0)
                ? this.getThemeColor(0)
                : '#fffff4',
            }}
          />
          <CardContent className={this.props.classes.pricePaper}>
            <Grid container>
              {this.state.studio.prices.map((item) => {
                return (
                  <Grid item xs={12} key={'price-' + item._id}>
                    <Grid
                      className={this.props.classes.priceRow}
                      container
                      justify="space-between"
                    >
                      <Grid xs={10} item>
                        <Typography>{item.text}</Typography>
                      </Grid>
                      <Grid align="right" xs={2} item>
                        {item.value}
                        {domain.currencyCode}
                      </Grid>
                    </Grid>
                    <Divider
                      style={{
                        backgroundColor: this.getThemeColor(0)
                          ? this.getThemeColor(0)
                          : '#fffff4',
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  renderRatePlans = () => {
    if (this.state.owner && this.state.studio) {
      return (
        <RatePlansView studio={this.state.studio} owner={this.state.owner} />
      );
    }
    return null;
  };

  renderPromotionEvents = () => {
    if (this.state.teachersMap) {
      return (
        <section id={'promotionEvents'} className={this.props.classes.section}>
          <ErrorBoundary>
            <Suspense fallback={LayoutHelper.renderLoader()}>
              <StudioPromotionEvents
                owner={this.state.owner}
                studio={this.state.studio}
                teachersMap={this.state.teachersMap}
                teachers={this.state.teachersMap}
              ></StudioPromotionEvents>
            </Suspense>
          </ErrorBoundary>
        </section>
      );
    }
    return null;
  };

  renderStudio = (studio: Studio) => {
    return (
      <div className="studio-panel container-fluid">
        {this.renderCallToAction()}
        <StudioHeader
          studio={studio}
          teachers={this.state.teachers}
          owner={this.state.owner}
          onTeachersClick={() => {
            this.setState({ showTeachers: true });
          }}
          onScheduleOpen={() => {
            this.setState({ showScheduleDialog: true });
          }}
          openContactStudio={this.openContactStudio}
        ></StudioHeader>
        {this.renderBody(studio)}
        {/*{this.props.domain.domain.adsense && <section className={this.props.classes.section}><AdComponent domain={this.props.domain.domain}/></section>}*/}
        {this.renderScheduleSection()}
        {/*{this.props.domain.domain.adsense && <section className={this.props.classes.section}><AdComponent domain={this.props.domain.domain}/></section>}*/}
        {this.renderContactStudio()}
        {this.renderAddReview()}
        <div
          style={{
            backgroundColor: this.getThemeColor(0)
              ? this.getThemeColor(0)
              : '#fffff4',
          }}
        >
          <section
            className={this.props.classes.section}
            id="studio-contacts"
            ref={this.googleMapRef}
          >
            <Typography
              className={this.props.classes.sectionHead}
              style={{ borderColor: `orange` }}
              variant="h4"
              component="h2"
            >
              {this.props.translate('studio.contacts')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                {this.state.studio?.hasLocation ? (
                  <ErrorBoundary>
                    <Suspense fallback={LayoutHelper.renderLoader()}>
                      <VooptyMapComponent
                        lat={this.state.studio.geo_with_lat_lon?.lat}
                        lng={this.state.studio.geo_with_lat_lon?.lon}
                        containerElement={<div style={{ height: `500px` }} />}
                        mapElement={
                          <div style={{ height: `100%` }} id="google-map-row" />
                        }
                      />
                    </Suspense>
                  </ErrorBoundary>
                ) : ( '' )}
              </Grid>
              <Grid
                className={this.props.classes.contactsArea}
                item
                xs={12}
                md={5}
              >
                <ul className={this.props.classes.socialIcons}>
                  <li>
                    {this.state.studio.viber && (
                      <Link
                        className="viber"
                        href={`viber://chat?number=${this.state.studio.viber}`}
                      >
                        <Icon className={this.props.classes.icon}>
                          <img
                            alt="Viber"
                            src={`${this.props.domain.staticUrl}/viber.svg`}
                            height={32}
                            width={32}
                          />
                        </Icon>
                      </Link>
                    )}
                  </li>
                  <li>
                    {this.state.studio.telegram && (
                      <Link
                        className="telegram"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={this.state.studio.telegram}
                      >
                        <Icon className={this.props.classes.icon}>
                          <img
                            alt="Telegram"
                            src={`${this.props.domain.staticUrl}/telegram.svg`}
                            height={32}
                            width={32}
                          />
                        </Icon>
                      </Link>
                    )}
                  </li>
                  {this.renderFacebook(studio.facebook)}
                  {this.renderInstagram(studio.instagram)}
                  {this.renderTwitter(studio.twitter)}
                  {this.renderYoutube(studio.youtube)}
                </ul>
                <ul className={this.props.classes.contactsList}>
                  {this.renderPhone(studio.phone)}
                  {this.renderEmail(studio.email)}
                  {this.renderWebSite(studio.site)}
                  {this.renderAddress(studio)}
                </ul>
                <em className={this.props.classes.note}>
                  {studio.additionalContactInfo}
                </em>
                {this.renderContactStudioBtn()}
                <div className={this.props.classes.qrHold}>
                  <div id="qr-container"></div>
                </div>
              </Grid>
            </Grid>
          </section>
        </div>
        {/*{this.props.domain.domain.adsense && <section className={this.props.classes.section}><AdComponent domain={this.props.domain.domain}/></section>}*/}
        {this.renderOtherStudios()}
        {this.renderReviews()}
        {this.renderFooter()}
        {!this.state.showBookingDialog &&
          !this.state.showContactStudio &&
          !this.state.showAddReview && (
            <Fab
              size="large"
              color="primary"
              className={this.props.classes.signUpFab}
              variant="extended"
              onClick={() => {
                this.openBookingDialog();
              }}
            >
              {this.props.translate('studio.joinTheClass')}
            </Fab>
          )}
        {this.renderBookingDialod()}
      </div>
    );
  };

  openBookingDialog = () => {
    if (
      this.state.studio?.managedByVoopty &&
      !this.state.studio.showGenericDialogOnSignUp
    ) {
      this.setState({ showBookingDialog: true, showCallToAction: false });
    } else {
      this.openContactStudio();
    }
  };

  renderBookingDialod = () => {
    if (!this.state.studio) return null;
    
    return (
      <BookingDialog
        open={!!this.state.showBookingDialog}
        onAsk={() => {
          this.setState({ showBookingDialog: false, showContactStudio: true });
        }}
        onClose={() => {
          this.setState({ showBookingDialog: false });
        }}
        studio={this.state.studio}
        teachersMap={this.state.teachersMap}
        owner={this.state.owner}
      />
    );
  };

  renderScheduleSection = () => {
    if (this.state.teachersMap) {
      return (
        <section className={this.props.classes.section}>
          <ErrorBoundary>
            <Suspense fallback={LayoutHelper.renderLoader()}>
              <StudioScheduleView
                studio={this.state.studio}
                teachersMap={this.state.teachersMap}
                teachers={this.state.teachersMap}
                owner={this.state.owner}
              ></StudioScheduleView>
            </Suspense>
          </ErrorBoundary>
        </section>
      );
    }
    return null;
  };

  renderContactStudioBtn = () => {
    if (this.state.studio?.email) {
      return (
        <Button
          onClick={this.openContactStudio}
          fullWidth
          variant="contained"
          size="large"
          color="primary"
        >
          {this.props.translate('studio.contact')}
        </Button>
      );
    }
    return null;
  };

  renderContactStudio = () => {
    if (!this.state.studio) return null;
    
    return (
      <ContactStudioForm
        open={this.state.showContactStudio}
        studio={this.state.studio}
        onSuccess={() => {
          this.props.enqueueSnackbar(
            this.props.translate('contactStudio.done')
          );
          this.closeContactStudio();
        }}
        onClose={() => {
          this.setState({ showContactStudio: false });
        }}
      />
    );
  };

  renderAddReview = () => {
    if (!this.state.studio) return null;
    
    return (
      <AddReviewForm
        studio={this.state.studio}
        open={!!this.state.showAddReview}
        onSuccess={() => {
          this.props.enqueueSnackbar(
            this.props.translate('pendingReview.reviewAdded')
          );
          this.setState({ showAddReview: false });
          this.loadReviews(this.state.studio!);
        }}
        onClose={() => {
          this.setState({ showAddReview: false });
        }}
      />
    );
  };

  closeContactStudio = () => {
    this.setState({ showContactStudio: false });
  };

  openContactStudio = () => {
    this.setState({
      showCallToAction: false,
      showContactStudio: true,
      contactStudioInfo: this.newContactStudioInfo(),
    });
  };

  newContactStudioInfo = (): ContactStudioInfo => {
    return { domain: this.props.domain.domain.id };
  };

  renderReviews = () => {
    return (
      <section className={this.props.classes.section} id="reviews">
        {this.renderClientReviews()}
      </section>
    );
  };

  renderClientReviews = () => {
    if (this.state.clientReviews) {
      if (this.state.clientReviews.groupedReviews.length > 0) {
        return (
          <Fragment>
            <Grid container spacing={1} justify="space-between">
              <Grid item>
                <Typography
                  key="header"
                  className={this.props.classes.sectionHead}
                  style={{ borderColor: `orange` }}
                  variant="h4"
                  component="h2"
                >
                  {this.props.translate('studio.clientReviews')}
                </Typography>
              </Grid>
              <Grid item>{this.renderWriteReview()}</Grid>
              <Grid item xs={12}>
                {this.state.clientReviews.groupedReviews.map(
                  (groupedReview, index) => {
                    return (
                      <Card
                        className={this.props.classes.reviewCard}
                        key={index}
                      >
                        {groupedReview.reviews.map(
                          (clientReview, nestedIndex) => {
                            if (clientReview.reviewType === 'VerifiedClient') {
                              return this.renderClientReview(
                                clientReview,
                                nestedIndex
                              );
                            }
                            if (clientReview.reviewType === 'Client') {
                              return this.renderUnverifiedClientReview(
                                clientReview,
                                nestedIndex
                              );
                            }
                            if (clientReview.reviewType === 'Owner') {
                              return this.renderOwnerReview(
                                clientReview,
                                nestedIndex
                              );
                            }
                            return null;
                          }
                        )}
                      </Card>
                    );
                  }
                )}
              </Grid>
            </Grid>
          </Fragment>
        );
      } else {
        return (
          <Grid container spacing={1} justify="space-between">
            <Grid item>
              <Typography key="empty" variant="subtitle1">
                {this.props.translate('studio.noClientReviews')}
              </Typography>
            </Grid>
            <Grid item>{this.renderWriteReview()}</Grid>
          </Grid>
        );
      }
    } else {
      return LayoutHelper.renderLoader();
    }
  };

  renderWriteReview = () => {
    if (!this.props.domain.domain.custom) {
      return (
        <Fab
          color="primary"
          variant="extended"
          className={this.props.classes.reviewFab}
          onClick={() => {
            this.setState({ showAddReview: true });
          }}
        >
          {this.props.translate('studio.addReview')}
        </Fab>
      );
    }
    return null;
  };

  renderClientReview = (clientReview: Review, index: number) => {
    let starsNumber = clientReview.rank || 0;
    let createdMoment = moment.utc(clientReview.created);
    let stars = Array.from({ length: starsNumber }).map((_, i) => (
      <label key={i}>
        <StarIcon />
      </label>
    ));

    return (
      <Fragment key={`client-review-${index}`}>
        <CardHeader
          className={this.props.classes.reviewHead}
          avatar={
            index !== 0 ? (
              <ArrowUpward />
            ) : (
              clientReview.anonymous && <VisibilityOff />
            )
          }
          title={!clientReview.anonymous && clientReview.creatorName}
          subheader={createdMoment.format('ddd, DD MMM YYYY')}
        ></CardHeader>
        <CardContent>
          {index === 0 && (
            <div className={this.props.classes.rateInfo}>
              <div className={this.props.classes.studioRate}>{stars}</div>
              <Typography
                variant="body2"
                className={this.props.classes.verifiedReview}
              >
                <CheckIcon /> {this.props.translate('studio.trueClientReviews')}
              </Typography>
            </div>
          )}
          <Typography variant="body2">{clientReview.content}</Typography>
        </CardContent>
      </Fragment>
    );
  };

  renderUnverifiedClientReview = (clientReview: Review, index: number) => {
    let starsNumber = clientReview.rank || 0;
    let createdMoment = moment.utc(clientReview.created);
    let stars = Array.from({ length: starsNumber }).map((_, i) => (
      <label key={i}>
        <StarIcon />
      </label>
    ));

    return (
      <Fragment key={`unverified-review-${index}`}>
        <CardHeader
          className={this.props.classes.reviewHead}
          avatar={
            index !== 0 ? (
              <ArrowUpward />
            ) : (
              clientReview.anonymous && <VisibilityOff />
            )
          }
          title={!clientReview.anonymous && clientReview.creatorName}
          subheader={createdMoment.format('ddd, DD MMM YYYY')}
        ></CardHeader>
        <CardContent>
          {index === 0 && (
            <div className={this.props.classes.rateInfo}>
              <div className={this.props.classes.unverifiedStudioRate}>
                {stars}
              </div>
            </div>
          )}
          <Typography variant="subtitle1" color="textSecondary">
            {clientReview.content}
          </Typography>
        </CardContent>
      </Fragment>
    );
  };

  renderOwnerReview = (clientReview: Review, index: number) => {
    if (!this.state.studio) return null;
    
    let createdMoment = moment.utc(clientReview.created);
    return (
      <Fragment key={`owner-review-${index}`}>
        <CardHeader
          className={this.props.classes.reviewHead}
          avatar={<ArrowUpward />}
          title={
            <Link
              href={routes.studio({ domainName: this.state.studio.domainName })}
            >
              {this.state.studio.name}
            </Link>
          }
          subheader={createdMoment.format('ddd, DD MMM YYYY')}
        />
        <CardContent className={this.props.classes.ownerReviewContent}>
          <Typography variant="body2">{clientReview.content}</Typography>
        </CardContent>
      </Fragment>
    );
  };

  renderPrices = () => {
    let studio = this.state.studio;
    if (!studio || !studio.hasPricing) {
      return null;
    }
    
    return (
      <div
        style={{
          backgroundColor: this.getThemeColor(0)
            ? this.getThemeColor(0)
            : '#fffff4',
        }}
      >
        <section id="price" className={this.props.classes.pricing}>
          <Grid container spacing={3}>
            {this.renderRatePlans()}
            {this.renderPricesTable()}
            {this.renderTrialPricing()}
            {this.renderVooptyDiscountInnerPrice()}
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title={this.props.translate('studio.pricingMore')}
                />
                <Divider
                  style={{
                    backgroundColor: this.getThemeColor(0)
                      ? this.getThemeColor(0)
                      : '#fffff4',
                  }}
                />
                <CardContent>
                  <div dangerouslySetInnerHTML={{ __html: studio.pricing || '' }} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              {this.renderContactStudioBtn()}
            </Grid>
          </Grid>
        </section>
      </div>
    );
  };

  renderTrialPricing = () => {
    if (this.state.studio?.trialPricing) {
      return (
        <Grid item xs={12}>
          <Card>
            <CardHeader title={this.props.translate('studio.trialPricing')} />
            <Divider
              style={{
                backgroundColor: this.getThemeColor(0)
                  ? this.getThemeColor(0)
                  : '#fffff4',
              }}
            />
            <CardContent>
              <Typography>{this.state.studio.trialPricing}</Typography>
            </CardContent>
          </Card>
        </Grid>
      );
    }
    return null;
  };

  renderPhone = (phone?: string) => {
    if (phone) {
      return (
        <li>
          <Link href={`tel:${phone}`}>
            <PhoneIcon /> {phone}
          </Link>
        </li>
      );
    }
    return null;
  };

  renderEmail = (email?: string) => {
    if (email) {
      return (
        <li>
          <Link href={`mailto:${email}`}>
            <MailIcon /> {email}
          </Link>
        </li>
      );
    }
    return null;
  };

  renderAddress = (studio: Studio) => {
    if (studio.address) {
      return (
        <li>
          <span
            className="address-selector"
            onClick={() => {
              {
                /*$('#google-map-row')[0].scrollIntoView(true);*/
              }
            }}
          >
            <LocationOnIcon /> {studio.address}
          </span>
        </li>
      );
    }
    return null;
  };

  renderWebSite = (webSite?: string) => {
    if (webSite) {
      return (
        <li>
          <Link href={webSite} target="_blank" rel="noopener noreferrer">
            <LanguageIcon /> {webSite}
          </Link>
        </li>
      );
    }
    return null;
  };

  renderFacebook = (facebook?: string) => {
    if (facebook) {
      return (
        <li>
          <Link className="facebook" href={facebook} target="_blank" rel="noopener noreferrer">
            <Icon className={this.props.classes.icon}>
              <img
                alt="Facebook"
                src={`${this.props.domain.staticUrl}/facebook.svg`}
                height={32}
                width={32}
              />
            </Icon>
          </Link>
        </li>
      );
    }
    return null;
  };

  renderInstagram = (instagram?: string) => {
    if (instagram) {
      return (
        <li>
          <Link className="instagram" href={instagram} target="_blank" rel="noopener noreferrer">
            <Icon className={this.props.classes.icon}>
              <img
                alt="Instagram"
                src={`${this.props.domain.staticUrl}/instagram.svg`}
                height={32}
                width={32}
              />
            </Icon>
          </Link>
        </li>
      );
    }
    return null;
  };

  renderTwitter = (twitter?: string) => {
    if (twitter) {
      return (
        <li>
          <Link className="twitter" href={twitter} target="_blank" rel="noopener noreferrer">
            <Icon className={this.props.classes.icon}>
              <img
                alt="Twitter"
                src={`${this.props.domain.staticUrl}/twitter.svg`}
                height={32}
                width={32}
              />
            </Icon>
          </Link>
        </li>
      );
    }
    return null;
  };

  renderYoutube = (youtube?: string) => {
    if (youtube) {
      return (
        <li>
          <Link className="youtube" href={youtube} target="_blank" rel="noopener noreferrer">
            <Icon className={this.props.classes.icon}>
              <img
                alt="YouTube"
                src={`${this.props.domain.staticUrl}/youtube.svg`}
                height={32}
                width={32}
              />
            </Icon>
          </Link>
        </li>
      );
    }
    return null;
  };

  renderBody = (studio: Studio) => {
    return (
      <Fragment>
        {!this.props.domain.domain.custom && (
          <section className={this.props.classes.aTags}>
            <div>
              {this.state.studio?.tags.map((tag, index) => {
                return (
                  <Link
                    key={index}
                    rel="nofollow"
                    href={`${routes.search()}?keyword=${tag}&location=${
                      this.state.studio?.city
                    },${this.state.studio?.country}`}
                  >
                    #{tag}
                  </Link>
                );
              })}
            </div>
          </section>
        )}
        {this.renderPromotionEvents()}
        <section className={this.props.classes.section} id={'aboutus'}>
          <Typography
            className={this.props.classes.sectionHead}
            style={{ borderColor: `orange` }}
            variant="h4"
            component="h2"
          >
            {this.props.translate('studio.aboutUs')}
          </Typography>
          {studio.shortInfo && (
            <Typography>
              <strong>{studio.shortInfo}</strong>
            </Typography>
          )}
          <div
            className={this.props.classes.aboutContent}
            dangerouslySetInnerHTML={{ __html: studio.description || '' }}
          ></div>
          <div className={this.props.classes.aboutContent}>
            {this.renderVooptyDiscount()}
          </div>
          {this.renderContactStudioBtn()}
        </section>
        {this.renderVideoReview(studio)}
        {/*{this.props.domain.domain.adsense && <section className={this.props.classes.section}><AdComponent domain={this.props.domain.domain}/></section>}*/}
        {this.renderTeachers()}
        {this.renderPrices()}
      </Fragment>
    );
  };

  renderVideoReview = (studio: Studio) => {
    if (studio.videoReview) {
      const url = studio.videoReview;
      const lastSlash = url.lastIndexOf('/');
      const embededUrl = [
        'https://www.youtube.com/embed',
        url.slice(lastSlash),
      ].join('');
      return (
        <div
          style={{
            backgroundColor: this.getThemeColor(0)
              ? this.getThemeColor(0)
              : '#fffff4',
          }}
        >
          <section className={this.props.classes.section}>
            <Grid container spacing={3} justify="center">
              <Grid align="center" item xs={8}>
                <div className={this.props.classes.videoWrapper}>
                  {/*<iframe width={LayoutHelper.isMobile() ? 280 : 840}*/}
                  {/*className={this.props.classes.vooptyVideo}*/}
                  {/*height={LayoutHelper.isMobile() ? 157 : 470}*/}
                  {/*src={embededUrl} frameborder="0"*/}
                  {/*allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"*/}
                  {/*allowfullscreen></iframe>*/}
                  <ErrorBoundary>
                    <Suspense fallback={LayoutHelper.renderLoader()}>
                      <YouTube
                        className={this.props.classes.vooptyVideo}
                        url={embededUrl}
                      />
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </Grid>
            </Grid>
          </section>
        </div>
      );
    }
    return null;
  };

  renderVooptyDiscount = () => {
    if (this.state.studio?.vooptyPromocode && !this.props.domain.domain.custom) {
      return (
        <Paper
          id={'vooptyDiscount'}
          className={this.props.classes.vooptyDiscount}
        >
          <Typography variant="h6">
            {this.props.translate('studio.promocode')}
          </Typography>
          {this.renderVooptyDiscountContent()}
        </Paper>
      );
    }
    return null;
  };

  renderVooptyDiscountInnerPrice = () => {
    if (this.state.studio?.vooptyPromocode && !this.props.domain.domain.custom) {
      return (
        <Grid item xs={12}>
          <Card>
            <CardHeader title={this.props.translate('studio.promocode')} />
            <Divider
              style={{
                backgroundColor: this.getThemeColor(0)
                  ? this.getThemeColor(0)
                  : '#fffff4',
              }}
            />
            <CardContent>{this.renderVooptyDiscountContent()}</CardContent>
          </Card>
        </Grid>
      );
    }
    return null;
  };

  renderVooptyDiscountContent = () => {
    if (UserHelper.isLoggedIn(this.props.user)) {
      return (
        <Fragment>
          <div
            dangerouslySetInnerHTML={{
              __html: this.state.studio?.vooptyPromocode || '',
            }}
          />
          <Typography variant="body2">
            {this.props.translate('studio.promocodeRules')}
            <Link href={routes.me()}>{this.props.translate('account.qr')}</Link>
          </Typography>
        </Fragment>
      );
    } else {
      return (
        <Fragment>
          <Typography variant="body2">
            <Link
              href={`${routes.clientLogin()}?path=${
                window.location.pathname
              }`}
            >
              {this.props.translate('studio.promocodeSignIn')}
            </Link>
            {this.props.translate('studio.promocodeCTA')}
          </Typography>
        </Fragment>
      );
    }
  };

  renderOtherStudios = () => {
    if (
      this.state.otherStudiosFromOwner &&
      this.state.otherStudiosFromOwner.length > 0
    ) {
      return (
        <section className={this.props.classes.section}>
          <Typography
            className={this.props.classes.sectionHead}
            style={{ borderColor: `orange` }}
            variant="h4"
            component="h2"
          >
            {this.props.translate('studio.branches')}
          </Typography>
          <Grid container spacing={3}>
            {this.state.otherStudiosFromOwner.map((otherStudio, index) => {
              return (
                <Grid item xs={12} sm={4} md={3} key={index}>
                  <Link
                    href={routes.studio({ domainName: otherStudio.domainName })}
                  >
                    <Card className={this.props.classes.itemCard}>
                      <CardActionArea
                        component="div"
                        className={this.props.classes.cardArea}
                      >
                        {otherStudio.avatar && (
                          <CardMedia
                            image={otherStudio.avatar}
                            title={otherStudio.name}
                            className={this.props.classes.media}
                          />
                        )}
                        <CardContent className={this.props.classes.cardContent}>
                          <Typography component="h3">
                            {otherStudio.name}
                          </Typography>
                          <div className={this.props.classes.address}>
                            <LocationOnOutlinedIcon />
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              component="span"
                            >
                              {otherStudio.address}
                            </Typography>
                          </div>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Link>
                </Grid>
              );
            })}
          </Grid>
        </section>
      );
    }
    return null;
  };

  renderTeachers = () => {
    if (this.state.teachers && this.state.teachers.length > 0) {
      return (
        <section className={this.props.classes.section} id="teachers">
          <Grid container spacing={1}>
            {' '}
            <Grid item xs={12}>
              <ExpansionPanel
                TransitionProps={{ unmountOnExit: true }}
                expanded={!!this.state.showTeachers}
                onChange={() => {
                  this.setState({ showTeachers: !this.state.showTeachers });
                }}
              >
                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography
                    className={this.props.classes.sectionHead}
                    style={{ borderColor: `orange` }}
                    variant="h4"
                    component="h2"
                  >
                    {this.props.translate('studio.teachers')}
                  </Typography>
                </ExpansionPanelSummary>
                <ExpansionPanelDetails>
                  <Grid container spacing={3}>
                    {this.state.teachers.map(this.renderTeacher)}
                  </Grid>
                </ExpansionPanelDetails>
              </ExpansionPanel>
            </Grid>
            <Grid item xs={12}>
              {this.state.studio?.teachersInfo && (
                <Typography>{this.state.studio.teachersInfo}</Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              {this.renderContactStudioBtn()}
            </Grid>
          </Grid>
        </section>
      );
    } else {
      if (this.state.studio?.teachersInfo) {
        return (
          <section className={this.props.classes.section} id="teachers">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  className={this.props.classes.sectionHead}
                  style={{ borderColor: `orange` }}
                  variant="h4"
                  component="h2"
                >
                  {this.props.translate('studio.teachers')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {this.state.studio.teachersInfo}
              </Grid>
              <Grid item xs={12}>
                {this.renderContactStudioBtn()}
              </Grid>
            </Grid>
          </section>
        );
      }
    }
    return null;
  };

  renderTeacher = (teacher: Teacher, index: number) => {
    let studioIds = teacher.studioIds;
    if (
      studioIds &&
      studioIds.length > 0 &&
      !studioIds.includes(this.state.studio?.id || '')
    ) {
      return null;
    }
    return (
      <Grid item xs={12} sm={4} md={3} key={index}>
        <Card className={this.props.classes.teacherBlock}>
          <CardMedia
            className={this.props.classes.teacherPhoto}
            image={teacher.avatar}
            title={teacher.name}
            style={{ borderColor: `orange` }}
          />
          <CardContent>
            <Typography component="h4">{teacher.name}</Typography>
            <Typography variant="body2" color="textSecondary">
              {teacher.title}
            </Typography>
            {teacher.description && (
              <article
                className={this.props.classes.teacherInfo}
                dangerouslySetInnerHTML={{ __html: teacher.description }}
              ></article>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  renderCategory = (categorySuggestions: StudioSuggestion, index: number, hrefBase: string, rel: string) => {
    let categoryName = categorySuggestions.category.name;
    let total = categorySuggestions.total;
    let parentCategoryName = categorySuggestions.category.parent.name;
    let items = categorySuggestions.items;
    const href = `${hrefBase}${categoryName}`;
    return (
      <Grid key={index} item xs={12} sm={6}>
        <Typography
          gutterBottom
          style={{ borderColor: `orange` }}
          variant="subtitle2"
        >
          {this.props.translate('footer.category', {
            total: total > 0 ? total : '',
          })}
          <Link href={href} rel={rel}>
            {this.props.translate('studio.category.' + parentCategoryName)}
            {'/'} {this.props.translate('studio.category.' + categoryName)}
          </Link>
        </Typography>
        <Grid
          container
          direction="row"
          justify="flex-start"
          alignItems="stretch"
          spacing={2}
          className={this.props.classes.studioList}
        >
          {items.map((item, index) => {
            return (
              <Grid item key={index}>
                <div className={this.props.classes.studioListItem}>
                  <Link
                    href={routes.studio({ domainName: item.domainName })}
                    rel={!item.managedByVoopty ? 'nofollow' : undefined}
                    target="_blank"
                  >
                    <Tooltip title={item.name}>
                      <img
                        data-src={item.avatar}
                        className="lazyload"
                        alt={item.name}
                      />
                    </Tooltip>
                  </Link>
                </div>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    );
  };

  renderBreadcramps = (hrefBase?: string, rel?: string) => {
    let otherSuggestions = this.state.otherSuggestions;

    if (otherSuggestions && hrefBase) {
      return (
        <Grid container spacing={2}>
          {otherSuggestions.map((category, index) => {
            return this.renderCategory(category, index, hrefBase, rel || '');
          })}
        </Grid>
      );
    }
    return null;
  };

  renderFooter = () => {
    if (!this.props.domain.domain.custom && this.state.studio) {
      let hrefBase;
      let regionLink;
      for (let i = 0; i < this.state.studio.regions.length; i++) {
        const region = this.state.studio.regions[i];
        if (region.level === 4) {
          const parents = region.parents;
          const parent = parents[0];
          if (parents) {
            regionLink = `/search/${parent}/${region.name}`;
          }
        }
      }
      if (!regionLink) {
        hrefBase = `${routes.search()}?location=${this.state.studio.city},${
          this.state.studio.country
        }`;
      }
      return (
        <footer className={this.props.classes.pageFooter}>
          <section className={this.props.classes.section}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                {this.renderBreadcramps(
                  regionLink ? `${regionLink}/` : `${hrefBase}&category=`,
                  regionLink ? 'tag' : 'nofollow'
                )}
              </Grid>
              <Grid item xs={12} sm={16}>
                <Link
                  rel={regionLink ? 'tag' : 'nofollow'}
                  href={regionLink ? regionLink : hrefBase}
                  style={{ display: `flex` }}
                >
                  <SearchIcon /> {this.props.translate('footer.search')}
                </Link>
              </Grid>
            </Grid>
          </section>
        </footer>
      );
    }
    return null;
  };
}

const styles = (theme: Theme) => createStyles({
  aTags: {
    maxWidth: (theme as any).width.sectionWidth,
    margin: 'auto 10px',
    height: '42px',
    overflow: 'hidden',
    padding: theme.spacing(0, 2),
    position: 'relative',
    fontSize: '10px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',

    '& a': {
      color: theme.palette.secondary.light,
      display: 'inline-block',
      marginRight: theme.spacing(0.5),
      textDecoration: 'none',
    },
  },
  sectionHead: {
    margin: theme.spacing(0, 0, 3),
    padding: theme.spacing(0, 0, 1),
    position: 'relative',
    display: 'inline-block',
    borderBottom: '2px solid transparent',
  },
  section: {
    maxWidth: (theme as any).width.sectionWidth,
    margin: '0 auto',
    padding: theme.spacing(6, 3),
  },
  pricing: {
    maxWidth: (theme as any).width.sectionWidth,
    margin: '0 auto',
    padding: theme.spacing(6, 3),

    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
    },
    '& a:hover': {
      textDecoration: 'underline',
    },
  },
  pricePaper: {
    padding: '0 !important',
  },
  priceRow: {
    padding: theme.spacing(1, 2),
  },
  vooptyVideo: {
    width: '100%',
    margin: 'auto',
  },
  aboutContent: {
    marginBottom: theme.spacing(2),
    overflow: 'hidden',

    '& img': {
      maxWidth: '100%',
    },

    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
    },

    '& a:hover': {
      textDecoration: 'underline',
    },
  },
  ownerReviewContent: { marginLeft: theme.spacing(4) },
  appBar: {
    position: 'relative',
  },
  mainTypography: { padding: theme.spacing(2) },
  signUpFab: {
    position: 'fixed',
    right: theme.spacing(3),
    bottom: theme.spacing(3),
    zIndex: 10000,
    animationName: '$pulse-dot',
    animationDuration: '2s',
    animationTimingFunction: 'cubic-bezier(.37,0,.8,.77)',
    animationIterationCount: 'infinite',
    backgroundColor: 'rgb(28, 150, 63)',
  },
  reviewFab: {
    animationName: '$pulse-dot',
    animationDuration: '2s',
    animationTimingFunction: 'cubic-bezier(.37,0,.8,.77)',
    animationIterationCount: 'infinite',
  },
  callToActionGrid: {
    position: 'absolute',
    width: 280,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    outline: 'none',
  },
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconA: {
    display: 'inline-block',
    width: '40px',
    height: '40px',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  },
  iconLi: {
    display: 'inline-block',
    padding: theme.spacing(0, 1),
  },
  iconjump: {
    margin: '0',
    padding: theme.spacing(2, 0, 0),
    listStyle: 'none',
    textAlign: 'center',

    '& > li:first-child': {
      animation: 'jump 3s ease 0s infinite normal',
    },

    '& > li:nth-child(2)': {
      animation: 'jump 3s ease .5s infinite normal',
    },

    '& > li:nth-child(3)': {
      animation: 'jump 3s ease 1s infinite normal',
    },
  },
  ctaIconHold: {
    margin: theme.spacing(0),
    padding: theme.spacing(0),
    listStyle: 'none',
  },
  itemCard: {
    minHeight: '100%',
    display: 'flex',
  },
  cardArea: {
    minHeight: '100%',
    position: 'relative',
    paddingBottom: theme.spacing(3),
  },
  cardContent: {
    padding: theme.spacing(2, 2, 1),
  },
  media: {
    height: 100,
    maxWidth: 160,
    backgroundSize: 'contain',
    margin: '20px auto 0',
  },
  address: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',

    '& > svg': {
      fontSize: '1rem',
    },
    '& > span': {
      fontSize: '12px',
    },
  },
  gridList: {
    flexWrap: 'nowrap',
    transform: 'translateZ(0)',
  },
  vooptyDiscount: {
    padding: theme.spacing(2),
    color: 'rgb(102, 60, 0)',
    border: '2px solid',
    borderColor: theme.palette.primary.main,
    borderRadius: 0,

    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
    },

    '& a:hover': {
      textDecoration: 'underline',
    },
  },
  promoName: {
    fontWeight: 900,
  },
  contactsArea: {
    '& a': {
      textDecoration: 'none',
      color: theme.palette.primary.dark,
    },

    '& a:hover': {
      textDecoration: 'underline',
    },
  },
  socialIcons: {
    margin: 0,
    padding: theme.spacing(0, 0, 2),
    listStyle: 'none',

    '& > li': {
      display: 'inline-block',
      padding: theme.spacing(0, 1, 0, 0),
    },
  },
  contactsList: {
    margin: '0',
    padding: theme.spacing(0, 0, 2),
    listStyle: 'none',

    '& li > *': {
      display: 'inline-flex',
    },
    '& svg': {
      marginRight: theme.spacing(0.5),
    },
  },
  note: {
    display: 'block',
    padding: theme.spacing(0, 0, 2),
    fontStyle: 'italic',
  },
  videoWrapper: {
    position: 'relative',
    paddingBottom: '56.25%',
    height: 0,
    '& > div': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
  },
  teacherInfo: {
    height: '100%',
    width: '100%',
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    padding: theme.spacing(2),
    overflowY: 'auto',
    backgroundColor: theme.palette.background.paper,
    opacity: 0,
    transition: 'all 0.2s ease-out',

    '& p': {
      margin: theme.spacing(0, 0, 1),
    },
  },
  teacherBlock: {
    minHeight: '100%',
    maxWidth: '335px',
    margin: '0 auto',
    position: 'relative',

    '&:hover article': {
      opacity: '.98 !important',
    },
  },
  teacherPhoto: {
    height: 1,
    width: '100%',
    paddingBottom: '99%',
    backgroundSize: 'cover',
    backgroundPosition: 'top center',
    borderBottom: '2px solid transparent',
  },

  reviewCard: {
    marginBottom: theme.spacing(2),
    position: 'relative',

    '& a': {
      textDecoration: 'none',
      color: theme.palette.primary.main,
    },

    '& a:hover': {
      textDecoration: 'underline',
    },
  },
  reviewHead: {
    paddingBottom: 0,
    fontSize: '14px',

    '& span': {
      fontSize: '1em',
    },

    '& a': {
      color: theme.palette.primary.dark,
    },
  },
  rateInfo: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(2),
  },
  studioRate: {
    color: theme.palette.primary.main,
    textAlign: 'right',

    '& label': {
      display: 'inline-block',
    },
    '& svg': {
      display: 'inline-block',
      fontSize: '1.4em',
    },
  },
  unverifiedStudioRate: {
    color: theme.palette.primary.main,
    textAlign: 'right',

    '& label': {
      display: 'inline-block',
    },
    '& svg': {
      display: 'inline-block',
      fontSize: '1.2em',
    },
  },
  verifiedReview: {
    color: 'green',
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '12px',

    '& svg': {
      marginRight: theme.spacing(0.5),
      fontSize: '1.2em',
    },
  },
  unverifiedReview: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '12px',

    '& svg': {
      marginRight: theme.spacing(0.5),
      fontSize: '1.2em',
    },
  },
  pageFooter: {
    background: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    borderBottom: '2px solid transparent',
    borderBottomColor: theme.palette.primary.main,

    '& a': {
      textDecoration: 'underline',
      color: theme.palette.secondary.contrastText,
    },

    '& a:hover': {
      textDecoration: 'none',
    },
  },
  studioList: {
    marginBottom: theme.spacing(3),
  },
  studioListItem: {
    backgroundColor: theme.palette.background.paper,
    width: '60px',
    height: '60px',
    padding: theme.spacing(0.5),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',

    '& img': {
      maxWidth: '100%',
      maxHeight: '52px',
      display: 'block',
    },
  },
  qrHold: {
    padding: theme.spacing(3),

    '& canvas': {
      display: 'block',
      margin: '0 auto',
    },
  },
  '@keyframes pulse-dot': {
    '0%': {
      transform: 'scale(1.2)',
    },
    '50%': {
      transform: 'scale(1)',
    },
    '100%': {
      transform: 'scale(1.2)',
    },
  },
  icon: {}
});

const mapStateToProps = (state: any) => ({
  translate: getTranslate(state.locale),
  currentLanguage: getActiveLanguage(state.locale).code,
  user: state.user,
  studio: state.studio,
  domain: state.domain,
});

const mapDispatchToProps = (dispatch: any) => {
  return {
    registerToClass: (studio: Studio, classId: string) => {
      if (studio.domainName) {
        dispatch(
          push(`${routes.class()}?keyword=${classId}&location=${studio.city},${studio.country}`)
        );
      } else {
        dispatch(push(`${routes.classId()}?keyword=${classId}`));
      }
    },
    toStudio: (studio: Studio) => {
      dispatch(push(routes.studio({ domainName: studio.domainName })));
    },
  };
};

export default withStyles(styles)(withSnackbar(connect(mapStateToProps, mapDispatchToProps)(StudioView)));